import { useMemo, useState } from 'react';
import { CASE_STUDY_BLOCKS, CASE_STUDY_SOURCES, caseStudyForStudent, caseStudyProgress, saveCaseStudyBlock, studentRegistrationContext } from './case-study.js';

function sameBlock(draft, saved) {
  const draftSources = [...draft.sources].sort().join('|');
  const savedSources = [...(saved.sources || [])].sort().join('|');
  return draft.summary.trim() === String(saved.summary || '').trim()
    && draft.notes.trim() === String(saved.notes || '').trim()
    && draftSources === savedSources;
}

function BlockEditor({ definition, savedBlock, onSave }) {
  const [draft, setDraft] = useState(() => ({
    summary: savedBlock.summary || '',
    notes: savedBlock.notes || '',
    sources: savedBlock.sources?.length ? savedBlock.sources : (savedBlock.summary ? [] : ['aee_teacher'])
  }));
  const changed = !sameBlock(draft, savedBlock);
  const toggleSource = (sourceId) => setDraft((current) => ({
    ...current,
    sources: current.sources.includes(sourceId)
      ? current.sources.filter((id) => id !== sourceId)
      : [...current.sources, sourceId]
  }));

  return <article className='case-study-block'>
    <header>
      <div><h3>{definition.title}</h3><p>{definition.prompt}</p></div>
      <span className={savedBlock.summary ? 'case-study-status saved' : 'case-study-status'}>{savedBlock.summary ? 'Salvo' : 'Pendente'}</span>
    </header>
    <label>Resumo do bloco
      <textarea rows='3' maxLength='800' value={draft.summary} onChange={(event) => setDraft({ ...draft, summary: event.target.value })} placeholder='Escreva uma síntese curta e observável.' />
    </label>
    <details className='case-study-details'>
      <summary>Perguntas de apoio e fontes</summary>
      <div className='case-study-questions'>{definition.questions.map((question) => <p key={question}>• {question}</p>)}</div>
      <fieldset><legend>Quem contribuiu com este bloco?</legend><div className='case-study-sources'>{CASE_STUDY_SOURCES.map((source) => <label key={source.id} className='checkbox-item'><input type='checkbox' checked={draft.sources.includes(source.id)} onChange={() => toggleSource(source.id)} /> {source.label}</label>)}</div></fieldset>
      <label>Observações complementares (opcional)
        <textarea rows='3' maxLength='1500' value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} placeholder='Contextos, exemplos ou pontos que precisam ser confirmados.' />
      </label>
    </details>
    <button type='button' className='case-study-save' disabled={!changed} onClick={() => onSave(definition.id, draft)}>{savedBlock.summary ? 'Salvar alterações' : 'Salvar bloco'}</button>
  </article>;
}

export function StudentCaseStudy({ student, data, setData, user, onSave }) {
  const study = caseStudyForStudent(data.caseStudies, student.id);
  const progress = caseStudyProgress(study);
  const registrationContext = useMemo(() => studentRegistrationContext(student), [student]);
  const [feedback, setFeedback] = useState('');

  function saveBlock(blockId, draft) {
    const updatedAt = new Date().toISOString();
    const caseStudies = saveCaseStudyBlock(data.caseStudies, { studentId: student.id, blockId, ...draft, authorId: user?.uid || '', updatedAt });
    const next = { ...data, caseStudies };
    setData(next);
    onSave?.(next);
    setFeedback(`Bloco “${CASE_STUDY_BLOCKS.find((item) => item.id === blockId)?.title}” salvo.`);
  }

  return <section className='card case-study'>
    <div className='case-study-heading'>
      <div><h2>Estudo de caso</h2><p className='hint'>Comece por qualquer bloco. Salve um resumo de cada vez e complete em outros encontros, sem precisar preencher tudo agora.</p></div>
      <strong>{progress.completed}/{progress.total} blocos</strong>
    </div>
    <div className='case-study-progress' aria-label={`${progress.percentage}% do estudo de caso preenchido`}><i style={{ width: `${progress.percentage}%` }} /></div>
    {registrationContext.length > 0 && <aside className='case-study-reuse'><h3>Informações já cadastradas</h3><p>Use como referência; não repita esses dados nos resumos, a menos que precise contextualizá-los.</p><div>{registrationContext.map((item) => <span key={item.id}><b>{item.label}:</b> {item.value}</span>)}</div></aside>}
    <p className='case-study-principle'>O diagnóstico é apenas um subsídio. Organize potencialidades, barreiras, acessibilidade e apoios para orientar a avaliação pedagógica e o PAEE/PEI.</p>
    <div className='case-study-grid'>{CASE_STUDY_BLOCKS.map((definition) => <BlockEditor key={definition.id} definition={definition} savedBlock={study.blocks[definition.id]} onSave={saveBlock} />)}</div>
    <p className='case-study-feedback' role='status' aria-live='polite'>{feedback}</p>
  </section>;
}
